import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatBytes } from '@/entities/attachment';
import type { SnapshotPreview } from '@/entities/database';
import { isApiError } from '@/shared/api';
import { useErrorMessage } from '@/shared/i18n';
import { Button, Combobox, Dialog, Icons, TextField } from '@/shared/ui';

import styles from './Portability.module.css';

/** Must match the server's limit for one snapshot. */
export const MAX_SNAPSHOT_BYTES = 256 * 1024 * 1024;

export interface ImportArgs {
  file: File;
  destination: string;
  records: boolean;
  name: string;
  confidential: boolean;
  signal: AbortSignal;
  onProgress: (loaded: number, total: number) => void;
}

interface Props<R> {
  title: string;
  intro: string;
  /** The destinations to choose from (workspaces); one or none = no picker. */
  destinations?: readonly { value: string; label: string }[];
  /** The preselected destination. */
  initialDestination: string;
  pickerLabel?: string;
  /** Offer the "confidential" choice (a new database); a table has none of its own. */
  offerConfidential: boolean;
  /** Warn when a table goes into a database that is not confidential though the file says so. */
  warnSourceConfidential?: boolean;
  preview: (
    file: File,
    records: boolean,
    destination: string,
    signal: AbortSignal,
  ) => Promise<SnapshotPreview>;
  run: (args: ImportArgs) => Promise<R>;
  onImported: (result: R) => void;
  onClose: () => void;
}

type Phase = 'choose' | 'validating' | 'ready' | 'importing';

/**
 * Import from a file in the visible steps of the board import: choose the file, a read-only preview
 * (what it holds, what would be left out, problems), then an explicit confirmation. Nothing existing
 * is changed: the result is always something new, owned by the importer, with no access taken from
 * the file. Shared by "new database" and "new table".
 */
export function ImportSnapshotDialog<R>({
  title,
  intro,
  destinations,
  initialDestination,
  pickerLabel,
  offerConfidential,
  warnSourceConfidential = false,
  preview,
  run,
  onImported,
  onClose,
}: Props<R>) {
  const { t, i18n } = useTranslation();
  const getMessage = useErrorMessage();
  const input = useRef<HTMLInputElement>(null);
  const abort = useRef<AbortController | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [destination, setDestination] = useState(initialDestination);
  const [data, setData] = useState<SnapshotPreview | null>(null);
  const [name, setName] = useState('');
  const [records, setRecords] = useState(true);
  const [confidential, setConfidential] = useState(false);
  const [phase, setPhase] = useState<Phase>('choose');
  const [percent, setPercent] = useState(0);
  const [problem, setProblem] = useState<unknown>(null);

  const problems: { path: string; code: string }[] =
    isApiError(problem) && Array.isArray(problem.details?.['problems'])
      ? (problem.details['problems'] as { path: string; code: string }[])
      : (data?.problems ?? []);

  const check = (selected: File, withRecords: boolean, first: boolean, where = destination) => {
    abort.current?.abort();
    abort.current = new AbortController();
    setProblem(null);
    setPhase('validating');
    preview(selected, withRecords, where, abort.current.signal).then(
      (result) => {
        setData(result);
        if (first) {
          setName(result.name);
          setConfidential(result.source_confidential);
          setRecords(result.includes_records);
        }
        setPhase('ready');
      },
      (error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setData(null);
        setProblem(error);
        setPhase('choose');
      },
    );
  };

  const choose = (selected: File | undefined) => {
    if (!selected) return;
    setFile(selected);
    setData(null);
    if (selected.size > MAX_SNAPSHOT_BYTES) {
      setPhase('choose');
      setProblem(
        new Error(
          t('databases.portability.import.fileTooLarge', {
            limit: formatBytes(MAX_SNAPSHOT_BYTES, i18n.language),
          }),
        ),
      );
      return;
    }
    check(selected, true, true);
  };

  const start = () => {
    if (!file || !data?.valid || phase !== 'ready' || name.trim() === '') return;
    setProblem(null);
    setPhase('importing');
    setPercent(0);
    abort.current = new AbortController();
    run({
      file,
      destination,
      records: records && data.includes_records,
      name: name.trim(),
      confidential,
      signal: abort.current.signal,
      onProgress: (loaded, total) => {
        setPercent(total > 0 ? Math.round((loaded / total) * 100) : 0);
      },
    }).then(onImported, (error: unknown) => {
      if (!(error instanceof DOMException && error.name === 'AbortError')) setProblem(error);
      setPhase('ready');
    });
  };

  const close = () => {
    abort.current?.abort();
    onClose();
  };

  const busy = phase === 'validating' || phase === 'importing';
  const describe = (error: unknown) =>
    error instanceof Error && !isApiError(error) ? error.message : getMessage(error);

  return (
    <Dialog title={title} onClose={close}>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          start();
        }}
      >
        <p className={styles.intro}>{intro}</p>
        {destinations && destinations.length > 1 && (
          <Combobox
            label={pickerLabel ?? ''}
            value={destination}
            options={[...destinations]}
            disabled={busy}
            onChange={(next) => {
              setDestination(next);
              if (file) check(file, records, false, next);
            }}
          />
        )}
        <input
          ref={input}
          type="file"
          className={styles.fileInput}
          accept=".json,application/json"
          aria-label={t('databases.portability.import.fileLabel')}
          tabIndex={-1}
          onChange={(event) => {
            choose(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
        <div className={styles.actions}>
          <Button
            disabled={busy}
            onClick={() => {
              input.current?.click();
            }}
          >
            <Icons.import />{' '}
            {file
              ? t('databases.portability.import.chooseAnother')
              : t('databases.portability.import.choose')}
          </Button>
          {file && (
            <span className={styles.meta}>
              {t('databases.portability.import.fileInfo', {
                name: file.name,
                size: formatBytes(file.size, i18n.language),
              })}
            </span>
          )}
        </div>

        {phase === 'validating' && (
          <p className={styles.note} role="status">
            {t('databases.portability.import.validating')}
          </p>
        )}
        {problem !== null && (
          <p className={styles.error} role="alert">
            {describe(problem)}
          </p>
        )}
        {problems.length > 0 && (
          <ul className={styles.problems} aria-label={t('databases.portability.import.problems')}>
            {problems.slice(0, 20).map((item) => (
              <li key={`${item.path}${item.code}`}>
                <code>{item.path}</code> —{' '}
                {t(`databases.portability.problem.${item.code}`, { defaultValue: item.code })}
              </li>
            ))}
          </ul>
        )}

        {data && (phase === 'ready' || phase === 'importing') && (
          <>
            <h3 className={styles.sectionTitle}>{t('databases.portability.import.preview')}</h3>
            <ul className={styles.counts}>
              <li>
                {t('databases.portability.import.format', {
                  kind: t(`databases.portability.kind.${data.kind}`),
                  version: data.version,
                })}
                {data.exported_at &&
                  ` · ${t('databases.portability.import.exportedAt', {
                    when: new Date(data.exported_at).toLocaleString(i18n.language),
                  })}`}
              </li>
              <li>{t('databases.portability.import.tables', { count: data.tables.length })}</li>
              <li>{t('databases.portability.import.columns', { count: data.columns })}</li>
              <li>
                {data.includes_records
                  ? t('databases.portability.import.records', { count: data.records })
                  : t('databases.portability.import.noRecords')}
              </li>
            </ul>
            {data.dropped_user_references > 0 && (
              <p className={styles.warning} role="note">
                {t('databases.portability.import.people', {
                  count: data.dropped_user_references,
                })}
              </p>
            )}
            {warnSourceConfidential && data.source_confidential && (
              <p className={styles.warning} role="note">
                {t('databases.portability.import.sourceConfidential')}
              </p>
            )}
            <TextField
              label={t('databases.portability.import.name')}
              value={name}
              maxLength={100}
              disabled={phase === 'importing'}
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
            {data.includes_records && (
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={records}
                  disabled={busy}
                  onChange={(event) => {
                    setRecords(event.target.checked);
                    if (file) check(file, event.target.checked, false);
                  }}
                />
                <span>
                  <strong>{t('databases.portability.import.withRecords')}</strong>
                </span>
              </label>
            )}
            {offerConfidential && (
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={confidential}
                  disabled={busy}
                  onChange={(event) => {
                    setConfidential(event.target.checked);
                  }}
                />
                <span>
                  <strong>{t('databases.confidential')}</strong>
                  <span className={styles.note}>
                    {data.source_confidential
                      ? t('databases.portability.import.confidentialClaimed')
                      : t('databases.confidentialHint')}
                  </span>
                </span>
              </label>
            )}
            <p className={styles.notice} role="note">
              <Icons.lock /> {t('databases.portability.import.notice')}
            </p>
          </>
        )}

        {phase === 'importing' && (
          <div role="status">
            <p className={styles.note}>
              {percent < 100
                ? t('databases.portability.import.uploading', { percent })
                : t('databases.portability.import.importing')}
            </p>
            <progress className={styles.progress} max={100} value={percent} />
          </div>
        )}

        <div className={styles.actions}>
          <Button
            type="submit"
            variant="primary"
            disabled={phase !== 'ready' || !data?.valid || name.trim() === ''}
          >
            {t('databases.portability.import.confirm')}
          </Button>
          <Button type="button" variant="ghost" onClick={close}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
